import { useMemo, useState } from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { isEmpty } from 'lodash';
import DataTable from 'src/components/common/DataTables/DataTable';
import { ALL_FAILED_ORDER_STATUS, ROLES } from 'src/constants';
import ShipmentFailReasonDialog from 'src/_modules/Orders/components/ShipmentFailReasonDialog';
import { useSelector } from 'react-redux';

const TrackingNumberCell = ({ row }) => {
    const shipment = row?.shipment;
    const trackingUrl = shipment?.trackingUrl;
    const trackingNumber = shipment?.trackingNumber;

    if (!trackingNumber) {
        return <Typography variant="body2">---</Typography>;
    }

    if (trackingUrl) {
        return (
            <Link
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: 'primary.main',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    {trackingNumber}
                </Typography>
            </Link>
        );
    }

    return <Typography variant="body2">{trackingNumber}</Typography>;
};

const DescriptionCell = ({ row }) => {
    const description = row?.description;

    if (!description) {
        return <Typography variant="body2">---</Typography>;
    }

    function getDirection(text) {
        return /[\u0600-\u06FF]/.test(text) ? "rtl" : "ltr";
    }


    return (
        <Tooltip title={description} placement="top">
            <Typography
                variant="body2"
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '250px',
                    unicodeBidi: "plaintext",
                    direction: getDirection(description),
                    textAlign: getDirection(description) === "rtl" ? "right" : "left",
                }}
            >
                {description}
            </Typography>
        </Tooltip>
    );
};

const MapitShipStatusTrackGrid = ({
    statusTrack = [],
    orderTimelineStatus,
    t,
    isMapitShip = false,
    isAdmin = false,
    onStatusClick,
}) => {
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const { profile } = useSelector((state) => state.entities.profile)

    const rows = statusTrack.map((item, index) => ({
        id: item?._id || index,
        dateTime: item?.dateTime,
        status: item?.status,
        shipment: item?.shipment,
        description: item?.description,
        userType: item?.userType,
        statusIndex: index,
        by: item?.by,
    }));

    // Calculate pagination
    const totalRows = rows.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedRows = rows.slice(startIndex, endIndex);

    const columns = useMemo(
        () => [
            {
                field: 'dateTime',
                headerName: t('Date'),
                width: 180,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {moment(params.value).format('DD/MM/YYYY - hh:mm a')}
                    </Typography>
                ),
            },
            {
                field: 'status',
                headerName: t('Status'),
                width: 200,
                renderCell: (params) => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                            cursor: isAdmin && onStatusClick ? 'pointer' : 'default',
                        }}
                        onClick={() => {
                            if (isAdmin && onStatusClick) {
                                onStatusClick(statusTrack[params.row.statusIndex]);
                            }
                        }}
                    >
                        {isMapitShip && ALL_FAILED_ORDER_STATUS.includes(params.value) && (
                            <ShipmentFailReasonDialog failedStatus={statusTrack[params.row.statusIndex]} />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {(isMapitShip && ALL_FAILED_ORDER_STATUS.includes(params.value))
                                ? t("Shipment creation failed")
                                : orderTimelineStatus(params.value, params.row.statusIndex)}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'shippingCompany',
                headerName: t('Shipping Company'),
                width: 180,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {params.row.shipment?.deliveryOptionName || '---'}
                    </Typography>
                ),
                sortable: false,
            },
            {
                field: 'trackingNumber',
                headerName: t('Tracking Number'),
                width: 200,
                renderCell: (params) => <TrackingNumberCell row={params.row} />,
                sortable: false,
            },
            {
                field: 'description',
                headerName: t('Description'),
                width: 280,
                renderCell: (params) => <DescriptionCell row={params.row} />,
                sortable: false,
            },
            ...(profile?.role === ROLES.admin
                ? [
                    {
                        field: 'userType',
                        headerName: t('Change by'),
                        width: 270,
                        renderCell: (params) => {
                            const userType = params.value;
                            const entity = params.row?.by;

                            if (!userType) {
                                return <Typography variant="body2">---</Typography>;
                            }


                            return (
                                <Typography variant="body2">
                                    {`${entity?.name || '-'} (${t(`userType.${userType}`) || userType})`}
                                </Typography>
                            );
                        },
                    },
                ]
                : []),
        ],
        [t, orderTimelineStatus, isAdmin, isMapitShip, onStatusClick, statusTrack, profile?.role]
    );

    if (isEmpty(statusTrack)) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">{t('No status track data available')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', mt: 1, height: "40vh" }}>
            <DataTable
                rows={paginatedRows}
                columns={columns}
                rowCount={totalRows}
                page={page}
                onPageChange={setPage}
                pageSize={pageSize}
                tableId="mapit-ship-status-track-table"
                customComponents={['Pagination']}
                sx={{
                    '& .MuiDataGrid-cell': {
                        py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeader': {
                        py: 1.5,
                        fontWeight: 600,
                    },
                }}
            />
        </Box>
    );
};

export default MapitShipStatusTrackGrid;
